import React from 'react'
import { findDOMNode } from 'react-dom'
import { DragLayer } from 'react-dnd';
import _ from 'lodash'
import PropTypes from 'prop-types'
import raf from 'raf'

const DEFAULT_BUFFER = 150

export function createHorizontalStrength (_buffer) {
  return function defaultHorizontalStrength ({ x, w }, point) {
    const buffer = Math.min(w / 2, _buffer)

    if (point.x >= x && point.x <= x + w) {
      if (point.x < x + buffer) {
        return (point.x - x - buffer) / buffer
      } else if (point.x > (x + w - buffer)) {
        return -(x + w - point.x - buffer) / buffer
      }
    }

    return 0
  }
}

export function createVerticalStrength (_buffer) {
  return function defaultVerticalStrength ({ y, h }, point) {
    const buffer = Math.min(h / 2, _buffer)

    if (point.y >= y && point.y <= y + h) {
      if (point.y < y + buffer) {
        return (point.y - y - buffer) / buffer
      } else if (point.y > (y + h - buffer)) {
        return -(y + h - point.y - buffer) / buffer
      }
    }

    return 0
  }
}

export const defaultHorizontalStrength = createHorizontalStrength(DEFAULT_BUFFER)

export const defaultVerticalStrength = createVerticalStrength(DEFAULT_BUFFER)

export class Scrollzone extends React.Component {
  static propTypes = {
    isDragging: PropTypes.bool.isRequired,

    forwardedRef: PropTypes.func,
    verticalStrength: PropTypes.func,
    horizontalStrength: PropTypes.func,
    vertical: PropTypes.bool,
    horizontal: PropTypes.bool,
    component: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.func
    ]),
    speed: PropTypes.number,
    className: PropTypes.string,
    scrollChangeHandler: PropTypes.func
  }

  static defaultProps = {
    verticalStrength: defaultVerticalStrength,
    horizontalStrength: defaultHorizontalStrength,
    speed: 30,
    component: 'div'
  }

  constructor (props, ctx) {
    super(props, ctx)

    this.scaleX = 0
    this.scaleY = 0
    this.frame = null

    this.handleDragOver = this.handleDragOver.bind(this)
    this.startScrolling = this.startScrolling.bind(this)
    this.stopScrolling = this.stopScrolling.bind(this)
    this.updateScrolling = _.throttle(this.updateScrolling.bind(this), 100, { trailing: false })
    this.handleScrollChange = this.handleScrollChange.bind(this)
  }

  componentWillUnmount () {
    if (this.frame) raf.cancel(this.frame)
  }

  handleDragOver (evt) {
    this.updateScrolling({
      clientX: evt.clientX,
      clientY: evt.clientY
    })
  }

  updateScrolling ({ clientX, clientY }) {
    const { left: x, top: y, width: w, height: h } = findDOMNode(this.containerRef).getBoundingClientRect()
    const box = { x, y, w, h }
    const coords = { x: clientX, y: clientY }

    // calculate strength
    this.scaleX = this.props.horizontal ? this.props.horizontalStrength(box, coords) : 0
    this.scaleY = this.props.vertical ? this.props.verticalStrength(box, coords) : 0

    // start scrolling if we need to
    if (!this.frame && (this.scaleX || this.scaleY)) this.startScrolling()
  }

  startScrolling () {
    const { speed } = this.props

    let i = 0
    const tick = () => {
      const { scaleX, scaleY } = this
      if (scaleX || scaleY) {
        // there's a bug in safari where it seems like we can't get
        // dragover events from a container that also emits a scroll
        // event that same frame. So we double the speed and only adjust
        // the scroll position at 30fps
        if (i++ % 2) {
          let scrollX = 0
          let scrollY = 0

          if (scaleX) scrollX = Math.floor(scaleX * speed)
          if (scaleY) scrollY = Math.floor(scaleY * speed)

          this.handleScrollChange(scrollX, scrollY)
        }
        this.frame = raf(tick)
      } else {
        this.stopScrolling()
      }
    }

    tick()
  }

  stopScrolling () {
    if (this.frame) {
      raf.cancel(this.frame)
      this.frame = null
      this.scaleX = 0
      this.scaleY = 0
    }
  }

  handleScrollChange (scrollX, scrollY) {
    const { scrollChangeHandler } = this.props

    if (scrollChangeHandler) {
      scrollChangeHandler(scrollX, scrollY, this.containerRef)
    } else {
      if (scrollX) findDOMNode(this.containerRef).scrollLeft += scrollX
      if (scrollY) findDOMNode(this.containerRef).scrollTop += scrollY
    }
  }

  render () {
    const {
      isDragging,

      forwardedRef,
      verticalStrength,
      horizontalStrength,
      vertical,
      horizontal,
      component: Component,
      speed,
      className,
      scrollChangeHandler,

      ...props
    } = this.props

    return (
      <Component
        {...props}
        ref={ref => {
          this.containerRef = ref

          if (forwardedRef) forwardedRef(ref)
        }}
        className={className}
        onDragOver={this.handleDragOver}
        onDragLeave={this.stopScrolling}
        onDragEnd={this.stopScrolling}
        onDragExit={this.stopScrolling}
        onDrop={this.stopScrolling}
      />
    )
  }
}

export const DragLayerScrollzone = DragLayer((monitor) => ({
  isDragging: monitor.isDragging()
}))(Scrollzone)

export default React.forwardRef((props, ref) => {
  return <DragLayerScrollzone {...props} forwardedRef={ref} />
})
